
import { createClient } from '@supabase/supabase-js'
import type { Database } from './database.types'
import type { Question } from '@/types/quiz'

const supabaseUrl = "https://fvoskhrltpygiduhzcgq.supabase.co"
const supabaseAnonKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZ2b3NraHJsdHB5Z2lkdWh6Y2dxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzk4MDc2MDMsImV4cCI6MjA1NTM4MzYwM30.XowgELGEGjvXy5AdW4mQlJD-NIsza1cumEUl8WO4MJ8"

// Get the current domain to set proper redirects with a more robust implementation
const getRedirectBase = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // Check if we're on localhost or a development environment
  if (hostname === 'localhost' || hostname.includes('.lovableproject.com')) {
    return window.location.origin;
  }
  
  // For production domains, always force www prefix for consistency with Supabase site URL config
  if (hostname === 'edaptica.com') {
    return 'https://www.edaptica.com';
  }
  
  // If already on www subdomain, return as is
  if (hostname.startsWith('www.')) {
    return window.location.origin;
  }
  
  // Default: add www. prefix to whatever domain we're on
  return `${protocol}//www.${hostname}`;
};

// Create a single instance of the Supabase client
export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      storage: localStorage,
      storageKey: 'edaptica-auth-token',
      // Debug options to help troubleshoot auth issues
      debug: true
    },
    global: {
      headers: {
        'X-Client-Info': 'supabase-js-web'
      }
    }
  }
)

// Define the shuffleArray helper function once at the module level
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Add the fetchQuizResults function
export async function fetchQuizResults(sessionId: string) {
  try {
    console.log(`Fetching quiz results for session: ${sessionId}`);
    
    const { data, error } = await supabase
      .from('quiz_results')
      .select('*')
      .eq('id', sessionId)
      .single();
      
    if (error) {
      console.error('Error fetching quiz results:', error);
      throw error;
    }

    if (!data) {
      console.log('No quiz results found for session:', sessionId);
      return null;
    }

    console.log(`Found quiz results for session: ${sessionId}`, {
      questions: data.questions?.length || 0,
      answers: data.answers?.length || 0
    });
    
    // The questions and answers are stored as JSON in the database
    let questions: Question[] = [];
    let answers: (number | null)[] = [];
    
    try {
      if (data.questions && typeof data.questions === 'object') {
        questions = data.questions as Question[];
        
        // Ensure subject and category are properly set for all questions
        questions = questions.map(q => ({
          ...q,
          subject: q.subject || data.subject, // Use question's subject or fallback to session subject
          category: q.category || data.category // Use question's category or fallback to session category
        }));
      }
      
      if (data.answers && typeof data.answers === 'object') {
        // Ensure answers are proper number or null values
        answers = (data.answers as any[]).map(a => 
          a === null ? null : Number(a)
        );
      }
    } catch (parseError) {
      console.error('Error parsing quiz data:', parseError);
    }
    
    console.log('Processed questions with subjects:', questions.map(q => q.subject));
    console.log('Processed questions with categories:', questions.map(q => q.category));
    
    return {
      questions,
      answers,
      subject: data.subject,
      category: data.category,
      difficulty: data.difficulty,
      score: data.score,
      total_questions: data.total_questions,
      percentage: data.percentage
    };
  } catch (error) {
    console.error('Error in fetchQuizResults:', error);
    throw error;
  }
}

export async function fetchSubjectsWithQuestions() {
  try {
    console.log('Fetching available subjects...');
    
    const categories = {
      "General Knowledge": ["History", "Geography", "Polity", "Economics", "Science"],
      "Engineering": ["Mechanical Engineering", "Civil Engineering", "Electrical Engineering",
                     "Electronics & Communication Engineering", "Computer Science Engineering"],
      "Data Science & AI": ["Machine Learning", "Deep Learning", "Data Science", "Big Data", 
                           "Artificial Intelligence"],
      "Government Exams": ["Quantitative Aptitude", "Reasoning", "English Language"]
    };
    
    const { data, error } = await supabase
      .from('questions')
      .select('Category')
      .not('Category', 'is', null)
      .not('Category', 'eq', '')
      .order('Category');

    if (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }

    const availableCategories = [...new Set(data.map(item => item.Category))]
      .filter(category => category && category.trim().length > 0)
      .sort();

    console.log('Available categories:', availableCategories);
    return availableCategories;
  } catch (error) {
    console.error('Error in fetchSubjectsWithQuestions:', error);
    throw error;
  }
}

export async function fetchQuestions(category: string, subject: string, difficulty: string = 'easy', limit: number = 200) {
  try {
    console.log('Starting to fetch questions with params:', { category, subject, difficulty, limit });
    
    // First, get globally used questions for this subject to exclude them
    let excludeQuestionIds: number[] = [];
    try {
      const { data: usedQuestions, error: usedError } = await supabase
        .from('used_questions_global')
        .select('question_id')
        .eq('subject', subject);
      
      if (!usedError && usedQuestions) {
        excludeQuestionIds = usedQuestions.map(uq => uq.question_id);
        console.log(`Found ${excludeQuestionIds.length} globally used questions to exclude for subject: ${subject}`);
      }
    } catch (usedError) {
      console.warn('Could not fetch globally used questions, proceeding without exclusion:', usedError);
    }
    
    // Build the query to exclude globally used questions AND filter by difficulty
    let query = supabase
      .from('questions')
      .select('*')
      .eq('Category', subject)
      .eq('Difficulty', difficulty); // Add difficulty filtering here
    
    // Exclude globally used questions if we have any
    if (excludeQuestionIds.length > 0) {
      query = query.not('ID', 'in', `(${excludeQuestionIds.join(',')})`);
    }
    
    const { data: questionsData, error: questionsError } = await query.limit(limit);
    
    if (questionsError) {
      console.error('Error fetching questions:', questionsError);
      throw questionsError;
    }

    console.log(`Found ${questionsData?.length || 0} unused questions for subject: ${subject} with difficulty: ${difficulty}`);
    
    // If we have very few questions left, consider resetting the global tracking
    if (questionsData && questionsData.length < Math.min(limit, 50)) {
      console.log(`Low question count (${questionsData.length}), may need to reset global tracking for subject: ${subject}`);
    }
    
    // Log difficulty distribution for debugging
    const difficultyCount = questionsData?.reduce((acc: Record<string, number>, q) => {
      const diff = q.Difficulty?.toLowerCase() || 'unknown';
      acc[diff] = (acc[diff] || 0) + 1;
      return acc;
    }, {});
    console.log('Question difficulty distribution:', difficultyCount);
    
    if (questionsData && questionsData.length > 0) {
      const validQuestions = transformQuestionsToFormat(questionsData);
      return validQuestions;
    }

    console.warn('No unused questions found in database for subject:', subject, 'with difficulty:', difficulty);
    return [];
  } catch (error) {
    console.error('Error in fetchQuestions:', error);
    throw error;
  }
}

// Fix the fetchQuizHistoryResults function to properly handle quiz history
export async function fetchQuizHistoryResults(sessionId: string) {
  try {
    console.log(`Fetching quiz history results for session: ${sessionId}`);
    
    // Get all question records for this session
    const { data: historyItems, error } = await supabase
      .from('user_quiz_history')
      .select('*')
      .eq('session_id', sessionId)
      .order('question_index', { ascending: true });
      
    if (error) {
      console.error('Error fetching quiz history:', error);
      throw error;
    }
    
    if (!historyItems || historyItems.length === 0) {
      console.log('No quiz history found for session:', sessionId);
      return null;
    }
    
    console.log(`Found ${historyItems.length} history items for session:`, sessionId);
    
    // Build a complete quiz result from the history items
    const questions = historyItems.map((item, index) => {
      // Convert the history item to a proper question format
      return {
        id: item.question_id || index,
        question: item.question_text,
        options: item.options || [],
        correctanswer: item.correct_option,
        subject: item.subject || 'General',
        category: item.category || 'General',
        difficulty: item.difficulty || 'medium',
        question_type: 'mcq'
      };
    });
    
    // Extract answers from the history items
    const answers = historyItems.map(item => item.selected_option);
    
    // Get basic metadata from the first item
    const firstItem = historyItems[0];
    
    const result = {
      questions: questions,
      answers: answers,
      subject: firstItem?.subject || null,
      category: firstItem?.category || null,
      difficulty: firstItem?.difficulty || 'medium',
      total_questions: questions.length,
      score: answers.filter((a, i) => a === questions[i]?.correctanswer).length,
      percentage: 0 // Will be calculated below
    };
    
    // Calculate percentage if we have valid questions
    if (questions.length > 0) {
      result.percentage = Math.round((result.score / questions.length) * 100);
    }
    
    console.log('Constructed quiz results from history:', result);
    return result;
  } catch (err) {
    console.error('Error in fetchQuizHistoryResults:', err);
    throw err;
  }
}

// Rewrite the saveQuizHistory function to properly save difficulty and maintain question order
export async function saveQuizHistory(
  sessionId: string, 
  questions: Question[], 
  answers: (number | null)[], 
  subject: string | null, 
  category: string | null,
  userId: string
) {
  try {
    console.log('Saving quiz history for session:', sessionId, 'with', questions.length, 'questions');
    console.log('Answers array:', answers);
    console.log('Questions difficulties:', questions.map(q => q.difficulty));
    console.log('Questions categories:', questions.map(q => q.category));
    console.log('Questions subjects:', questions.map(q => q.subject));
    console.log('Default subject:', subject);
    console.log('Default category:', category);
    
    if (!userId) {
      console.error('No user ID provided for saving quiz history');
      return false;
    }
    
    if (!sessionId) {
      console.error('No session ID provided for saving quiz history');
      return false;
    }
    
    if (!questions || questions.length === 0) {
      console.error('No questions provided for saving quiz history');
      return false;
    }
    
    if (!answers || answers.length === 0) {
      console.error('No answers provided for saving quiz history');
      return false;
    }
    
    // First, clean up any existing records for this session to avoid duplicates
    const { error: deleteError } = await supabase
      .from('user_quiz_history')
      .delete()
      .eq('session_id', sessionId);
      
    if (deleteError) {
      console.error('Error deleting existing quiz history:', deleteError);
      // Continue anyway as this is just a cleanup step
    }
    
    // Only process questions that have been answered
    const historyRecords = [];
    
    for (let i = 0; i < questions.length; i++) {
      if (i < answers.length && answers[i] !== null && answers[i] !== undefined && questions[i]) {
        // Create a deep copy of the question to ensure we're not modifying references
        const question = JSON.parse(JSON.stringify(questions[i]));
        const selectedOption = answers[i];
        const isCorrect = selectedOption === question.correctanswer;
        
        // Use the question's category and subject if available, otherwise use the provided defaults
        const questionCategory = question.category || category || 'General';
        const questionSubject = question.subject || subject || 'General';
        
        console.log(`Saving question ${i}: ID=${question.id}, Difficulty=${question.difficulty}, Category=${questionCategory}, Subject=${questionSubject}, Selected=${selectedOption}, Correct=${question.correctanswer}, isCorrect=${isCorrect}`);
        
        historyRecords.push({
          user_id: userId,
          session_id: sessionId,
          question_id: question.id,
          question_text: question.question,
          options: question.options,
          selected_option: selectedOption,
          correct_option: question.correctanswer,
          is_correct: isCorrect,
          difficulty: question.difficulty || 'medium', // Use question's own difficulty!
          subject: questionSubject,
          category: questionCategory,
          ai_explanation: question.explanation || null,
          question_index: i  // Store the original index to maintain question order
        });
      }
    }
    
    console.log('Prepared history records:', historyRecords.length);
    console.log('Difficulty values being saved:', historyRecords.map(r => r.difficulty));
    console.log('Category values being saved:', historyRecords.map(r => r.category));
    console.log('Subject values being saved:', historyRecords.map(r => r.subject));
    
    if (historyRecords.length === 0) {
      console.error('No answered questions to save');
      return false;
    }
    
    // Insert history records in batches if necessary (Supabase has limits on payload size)
    const BATCH_SIZE = 5; // Smaller batch size to reduce chance of errors
    const batches = [];
    
    for (let i = 0; i < historyRecords.length; i += BATCH_SIZE) {
      batches.push(historyRecords.slice(i, i + BATCH_SIZE));
    }
    
    console.log(`Inserting history in ${batches.length} batches`);
    
    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      console.log(`Inserting batch ${i+1}/${batches.length} with ${batch.length} records`);
      console.log('Batch difficulty values:', batch.map(r => r.difficulty));
      console.log('Batch category values:', batch.map(r => r.category));
      console.log('Batch subject values:', batch.map(r => r.subject));
      
      const { error } = await supabase
        .from('user_quiz_history')
        .insert(batch);
      
      if (error) {
        console.error(`Error saving quiz history batch ${i+1}:`, error);
        throw error;
      }
      
      console.log(`Successfully inserted batch ${i+1}`);
    }
    
    console.log('Quiz history saved successfully');
    return true;
  } catch (error) {
    console.error('Error in saveQuizHistory:', error);
    throw error;
  }
}

function transformQuestionsToFormat(questions: any[]): Question[] {
  return questions
    .map(q => {
      const options = [
        q["Option A"] || '',
        q["Option B"] || '',
        q["Option C"] || '',
        q["Option D"] || ''
      ].filter(Boolean);

      if (!q["Question"] || options.length < 4 || !q["Correct Answer"]) {
        console.warn('Skipping invalid question:', q["ID"]);
        return null;
      }

      const correctAnswer = q["Correct Answer"].trim();
      let correctAnswerIndex = -1;

      correctAnswerIndex = options.findIndex(opt => 
        opt.toLowerCase().trim() === correctAnswer.toLowerCase().trim()
      );

      if (correctAnswerIndex === -1 && /^[A-D]$/i.test(correctAnswer)) {
        correctAnswerIndex = ['A', 'B', 'C', 'D'].indexOf(correctAnswer.toUpperCase());
      }

      if (correctAnswerIndex === -1) {
        console.warn(`Could not find matching correct answer for question ${q["ID"]}:`, {
          correctAnswer,
          options
        });
        correctAnswerIndex = 0;
      }

      const correctOption = options[correctAnswerIndex];
      const shuffledOptions = shuffleArray(options);
      const newCorrectAnswerIndex = shuffledOptions.indexOf(correctOption);

      let explanation = q["Explanation"]?.trim() || '';
      if (explanation) {
        explanation = explanation
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/\n+/g, ' ')
          .trim();

        if (!explanation || explanation === correctAnswer) {
          explanation = `The correct answer is "${correctOption}". This is the most accurate option for this question.`;
        }
      }
      
      // Preserve the original difficulty from the database
      return {
        id: q["ID"],
        question: q["Question"].trim(),
        options: shuffledOptions,
        correctanswer: newCorrectAnswerIndex,
        difficulty: (q["Difficulty"]?.toLowerCase() || 'easy') as "easy" | "medium" | "hard",
        category: q["Category"],
        subject: q["Category"],
        explanation: explanation,
        hint: explanation,
        question_type: 'mcq' as const,
        ai_generated: q.status === 'ai_generated'
      };
    })
    .filter(Boolean);
}
